import { useEffect, useState } from 'react'
import { Card, LoadingRows, primaryBtn } from '../family-finance/shared'
import type { Position, RiskPreferences } from '../../types/fantasy-football'
import { POSITIONS } from '../../types/fantasy-football'
import { defaultRiskPreferences } from '../../lib/fantasy-knowledge-base'
import { saveRiskPreferences } from '../../lib/fantasy-api'
import type { FantasyState } from './useFantasy'

function Slider({
  label,
  value,
  min,
  max,
  step,
  hint,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  hint?: string
  onChange: (v: number) => void
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-slate-600 dark:text-slate-300 text-sm">{label}</label>
        <span className="text-slate-900 dark:text-white text-sm font-medium tabular-nums">{value.toFixed(2)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-blue-500"
      />
      {hint && <p className="text-slate-500 text-xs mt-0.5">{hint}</p>}
    </div>
  )
}

export default function RiskTab({ state }: { state: FantasyState }) {
  const { loading, prefs: savedPrefs, refresh } = state
  const [prefs, setPrefs] = useState<RiskPreferences>(savedPrefs)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => setPrefs(savedPrefs), [savedPrefs])

  async function handleSave() {
    setSaving(true)
    setSaved(false)
    try {
      await saveRiskPreferences(prefs)
      setSaved(true)
      refresh()
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingRows rows={6} />

  const set = (patch: Partial<RiskPreferences>) => {
    setSaved(false)
    setPrefs((p) => ({ ...p, ...patch }))
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card
          title="Risk appetite"
          subtitle="These feed directly into the valuation formulas — every ranking, recommendation, and draft order re-prices when you save"
        >
          <div className="space-y-5">
            <Slider
              label="Risk aversion (λ — F8)"
              value={prefs.riskAversion}
              min={0}
              max={2.5}
              step={0.05}
              hint="0 ranks purely on expected points; higher values pay more for consistency and penalize variance and correlated risk harder."
              onChange={(v) => set({ riskAversion: v })}
            />
            <Slider
              label="Teammate correlation weight (γ — F6)"
              value={prefs.teammateCorrelationWeight}
              min={0}
              max={2}
              step={0.05}
              hint="Scales the penalty (or credit) for rostering players on the same NFL team."
              onChange={(v) => set({ teammateCorrelationWeight: v })}
            />
            <Slider
              label="Bye-week overlap weight (F7)"
              value={prefs.byePenaltyWeight}
              min={0}
              max={2.5}
              step={0.05}
              hint="How much shared bye weeks cost. Raise it if punting a whole week is unacceptable in your league."
              onChange={(v) => set({ byePenaltyWeight: v })}
            />
            <Slider
              label="Schedule strength weight (θ — F4)"
              value={prefs.scheduleWeight}
              min={0}
              max={1}
              step={0.05}
              hint="How much preseason opponent-strength estimates move projections. 0 ignores schedule entirely."
              onChange={(v) => set({ scheduleWeight: v })}
            />
          </div>
        </Card>

        <Card
          title="Positional scarcity (F9)"
          subtitle="Your read on the league. Expect WRs to vanish from waivers mid-season? Weight WR above 1 and the whole board shifts."
        >
          <div className="space-y-5">
            {POSITIONS.map((pos: Position) => (
              <Slider
                key={pos}
                label={pos}
                value={prefs.positionScarcity[pos] ?? 1}
                min={0.5}
                max={1.8}
                step={0.05}
                onChange={(v) =>
                  set({ positionScarcity: { ...prefs.positionScarcity, [pos]: v } })
                }
              />
            ))}
          </div>
        </Card>
      </div>

      <Card
        title="Projection source trust (F1)"
        subtitle="Relative weights on each source feeding the consensus projection"
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {(['yahoo', 'espn', 'consensus'] as const).map((source) => (
            <Slider
              key={source}
              label={source === 'consensus' ? 'Expert consensus' : source === 'yahoo' ? 'Yahoo' : 'ESPN'}
              value={prefs.sourceWeights[source] ?? 1}
              min={0}
              max={3}
              step={0.1}
              onChange={(v) => set({ sourceWeights: { ...prefs.sourceWeights, [source]: v } })}
            />
          ))}
        </div>
      </Card>

      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={saving} className={primaryBtn}>
          {saving ? 'Saving…' : 'Save preferences'}
        </button>
        <button
          onClick={() => set(defaultRiskPreferences())}
          className="px-3 py-1.5 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 text-sm rounded-lg transition-colors"
        >
          Reset to defaults
        </button>
        {saved && <span className="text-emerald-600 dark:text-emerald-400 text-sm">Saved — rankings updated.</span>}
      </div>
    </div>
  )
}
